import {
	forwardRef,
	ReactElement,
	Ref,
	RefObject,
	useCallback,
	useEffect,
	useImperativeHandle,
	useRef,
	useState
} from "react";
import {
	ChangeTriggerKind,
	KeyEventArguments,
	SearchInputForm,
	SearchInputFormHandle,
	SearchPlaceholder
} from "client/Components";
import { setTrackingId } from "client/Misc/utils";
import { localize } from "client_legacy/ndaw/culture";
import { newGuid, Guid } from "client_legacy/ndaw/guid";
import { WorkspaceHeaderStyle } from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";

export type SearchFormHandle = {
	focusSearchInput(): void;
	blurSearchInput(): void;
};

export type SearchFormProps = {
	lastUsedSearchTerm: string;
	onSearch: (searchTerm: string) => void;
	onNavigateToResults: () => void;
	onFocusInput: () => void;
	effectiveHeaderStyle: WorkspaceHeaderStyle;
	onCloseResults: () => void;
};

export const SearchForm = forwardRef((props: SearchFormProps, ref: Ref<SearchFormHandle>): ReactElement => {
	const {
		lastUsedSearchTerm,
		onSearch,
		onNavigateToResults,
		onFocusInput,
		effectiveHeaderStyle,
		onCloseResults
	} = props;

	const [searchTerm, setSearchTerm] = useState<VersionedValue<string>>({
		value: lastUsedSearchTerm,
		version: newGuid()
	});

	const inputRef: RefObject<SearchInputFormHandle | null> = useRef<SearchInputFormHandle>(null);
	useImperativeHandle(
		ref,
		() => ({
			focusSearchInput: () => {
				inputRef.current?.focusInput();
			},
			blurSearchInput: () => {
				inputRef.current?.blurInput();
			}
		})
	);

	const searchHandler = useCallback(
		(value: string, version: Guid): void => {
			setSearchTerm({ value, version });
			onSearch(value);
		},
		[onSearch]
	);

	useEffect(
		(): void => {
			if (lastUsedSearchTerm) {
				inputRef.current
					?.focusInput();
			}

			setSearchTerm({
				value: lastUsedSearchTerm,
				version: newGuid()
			});
		},
		[lastUsedSearchTerm]
	);

	const onKeyDownHandler = useCallback(
		(args: KeyEventArguments): boolean => {
			if (args.key === "Escape") {
				onCloseResults();
				return true;
			}

			return false;
		},
		[onCloseResults]
	);

	return (
		<SearchInputForm
			value={searchTerm}
			onChange={searchHandler}
			trigger={{
				kind: ChangeTriggerKind.OnEnter
			}}
			ref={inputRef}
			onKeyDown={onKeyDownHandler}
			onNavigateToResults={onNavigateToResults}
			onFocus={() => onFocusInput()}
			placeholder={<Placeholder />}
			height="fill"
			trackingId="search-input"
			variant={
				effectiveHeaderStyle === WorkspaceHeaderStyle.ColoredBackground
					? "white"
					: undefined
			}
		/>
	);
});
SearchForm.displayName="SearchForm";


const Placeholder = (): ReactElement => (
	<span
		{...setTrackingId("search-input-placeholder")}
	>
		<SearchPlaceholder
			kind="search"
			properties={[localize("_AppsAnnouncementsFilesAndMore")]}
		/>
	</span>
);
Placeholder.displayName = "Placeholder";
